import { getCountFromServer, collection } from "firebase/firestore";
import { runtimeDb as db } from "../../firestoreStudio/runtime/runtimeDb";
import { toMillis } from "../explorer/explorerFilterUtils";

/**
 * Aggregate engine — COUNT / SUM / AVG / MIN / MAX / DISTINCT and GROUP BY,
 * computed over loaded/filtered explorer rows (SDK only). A server-side COUNT
 * helper is provided for whole-collection totals.
 */

export const AGGREGATE_OPS = [
  { id: "count", label: "COUNT", needsField: false },
  { id: "sum", label: "SUM", needsField: true, numeric: true },
  { id: "avg", label: "AVG", needsField: true, numeric: true },
  { id: "min", label: "MIN", needsField: true },
  { id: "max", label: "MAX", needsField: true },
  { id: "distinct", label: "DISTINCT", needsField: true },
];

function fieldValue(row, field) {
  return field === "id" ? row?.id : row?.data?.[field];
}

function toNumeric(value) {
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") {
    try { return value.toDate().getTime(); } catch { return NaN; }
  }
  const n = Number(value);
  return Number.isNaN(n) ? NaN : n;
}

function comparableValue(value) {
  const ms = toMillis(value);
  if (ms != null && typeof value !== "number") return ms;
  const n = toNumeric(value);
  return Number.isNaN(n) ? String(value ?? "") : n;
}

/** Compute a single aggregate over rows. */
export function aggregate(rows = [], { op = "count", field = "" } = {}) {
  if (op === "count") return { op, field, value: rows.length };

  const values = rows.map((r) => fieldValue(r, field)).filter((v) => v != null && v !== "");

  if (op === "distinct") {
    const set = new Set(values.map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v))));
    return { op, field, value: set.size, values: [...set].slice(0, 500) };
  }

  if (op === "sum" || op === "avg") {
    const nums = values.map(toNumeric).filter((n) => !Number.isNaN(n));
    const sum = nums.reduce((a, b) => a + b, 0);
    if (op === "sum") return { op, field, value: sum, count: nums.length };
    return { op, field, value: nums.length ? sum / nums.length : 0, count: nums.length };
  }

  if (op === "min" || op === "max") {
    if (!values.length) return { op, field, value: null };
    const sorted = [...values].sort((a, b) => {
      const ca = comparableValue(a);
      const cb = comparableValue(b);
      if (typeof ca === "number" && typeof cb === "number") return ca - cb;
      return String(ca).localeCompare(String(cb));
    });
    return { op, field, value: op === "min" ? sorted[0] : sorted[sorted.length - 1] };
  }

  return { op, field, value: null };
}

/**
 * GROUP BY groupField, computing an aggregate per group.
 * @returns {Array<{ group, count, value }>}
 */
export function groupBy(rows = [], { groupField = "", op = "count", field = "" } = {}) {
  const groups = new Map();
  rows.forEach((row) => {
    const rawKey = fieldValue(row, groupField);
    const key = rawKey == null || rawKey === "" ? "(empty)" : (typeof rawKey === "object" ? JSON.stringify(rawKey) : String(rawKey));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });

  const result = [];
  groups.forEach((groupRows, key) => {
    const agg = aggregate(groupRows, { op, field });
    result.push({ group: key, count: groupRows.length, value: agg.value });
  });

  return result.sort((a, b) => b.count - a.count);
}

/** Server-side whole-collection count (single aggregation read). */
export async function serverCount(collectionName) {
  const snap = await getCountFromServer(collection(db, collectionName));
  return snap.data().count;
}
